import { nanoid } from "nanoid";
import { useState } from "react";
import DOMPurify from "dompurify";
import { baseUrl } from "../../../baseUrl";
import ClearIcon from "@mui/icons-material/Clear";
import { fetchWithAuthHandling } from "../../../authHandling";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  List,
  Paper,
  Button,
  ListItem,
  TextField,
  Typography,
  IconButton,
  ListItemText
} from "@mui/material";

type Note = {
  created_by: {
    id: string;
    name: string;
  } | null;
  content: string | null;
  createdAt: Date | null | string;
};

const fetchMessages = async (buyerId: string): Promise<Note[]> => {
  const response = await fetchWithAuthHandling(
    `${baseUrl}/buyers/${buyerId}/notes`,
    {
      method: "GET",
      credentials: "include"
    }
  );

  if (!response.ok) throw new Error("Failed to fetch messages");

  const data = await response.json();

  return data.map((msg: Note) => ({
    created_by: msg.created_by
      ? { id: msg.created_by.id, name: msg.created_by.name }
      : { id: "", name: "Unknown" },
    content: msg.content ?? "No content available",
    createdAt: msg.createdAt ? new Date(msg.createdAt) : ""
  }));
};

const postMessage = async (
  newMessage: { content: string },
  buyerId: string
) => {
  const response = await fetchWithAuthHandling(
    `${baseUrl}/buyers/${buyerId}/notes`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(newMessage)
    }
  );

  if (!response.ok) throw new Error("Failed to post message");

  return await response.json();
};

interface NotesSectionProps {
  buyerId: string;
}

export const NotesSection = ({ buyerId }: NotesSectionProps) => {
  const [currentNote, setCurrentNote] = useState("");
  const queryClient = useQueryClient();

  const {
    data: messages = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ["getNotes", buyerId],
    queryFn: () => fetchMessages(buyerId),
    enabled: !!buyerId
  });

  const mutation = useMutation({
    mutationFn: (newMessage: { content: string }) =>
      postMessage(newMessage, buyerId),
    onSettled: async () => {
      setCurrentNote("");
      await queryClient.invalidateQueries({ queryKey: ["getNotes", buyerId] });
    }
  });

  const handleAddNote = () => {
    const trimmedNote = currentNote.trim();
    if (!trimmedNote) return;

    const sanitizedNote = DOMPurify.sanitize(trimmedNote);
    mutation.mutate({ content: sanitizedNote });
  };

  const handleClearNoteInput = () => {
    setCurrentNote("");
  };

  const formatTimestamp = (timestamp: Date | null) => {
    if (!timestamp) return "Unknown date";

    const now = new Date();
    const diffInSeconds = Math.floor(
      (now.getTime() - timestamp.getTime()) / 1000
    );

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;

    return new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(timestamp);
  };

  return (
    <Paper elevation={4} sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Notes
      </Typography>

      <Box display="flex" gap={1} alignItems="flex-start" sx={{ mb: 2 }}>
        <TextField
          fullWidth
          multiline
          variant="outlined"
          label="Content"
          value={currentNote}
          onChange={(e) => setCurrentNote(e.target.value)}
          rows={4}
        />
        {currentNote && (
          <IconButton
            onClick={handleClearNoteInput}
            aria-label="clear note text"
          >
            <ClearIcon />
          </IconButton>
        )}
      </Box>

      <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddNote}
          sx={{ fontWeight: "700", px: 4, py: 1 }}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Adding..." : "Add Note"}
        </Button>
      </Box>

      {isLoading ? (
        <Typography sx={{ mt: 2, textAlign: "center" }}>
          Loading notes...
        </Typography>
      ) : error ? (
        <Typography color="error" sx={{ mt: 2, textAlign: "center" }}>
          Failed to load notes.
        </Typography>
      ) : (
        messages.length > 0 && (
          <List
            sx={{
              mt: 2,
              maxHeight: 500,
              overflowY: "auto",
              border: "1px solid #ccc",
              borderRadius: 1,
              p: 1
            }}
          >
            {messages
              .sort(
                (a, b) =>
                  new Date(b.createdAt ?? 0).getTime() -
                  new Date(a.createdAt ?? 0).getTime()
              )
              .map((noteObj) => (
                <ListItem
                  key={nanoid()}
                  divider
                  sx={{
                    backgroundColor: "#F1F0F0",
                    borderRadius: 1,
                    color: "#333",
                    mb: 1,
                    alignItems: "flex-start"
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {noteObj.created_by?.name}
                        </Typography>
                        {noteObj.createdAt && (
                          <Typography variant="caption" sx={{ ml: 2 }}>
                            {formatTimestamp(new Date(noteObj.createdAt))}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body1"
                        sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                      >
                        {DOMPurify.sanitize(
                          noteObj.content ?? "No content available"
                        )}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
          </List>
        )
      )}
    </Paper>
  );
};
